import React, { useCallback, useMemo, useState } from 'react';
import { isNull, noop } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { setAccessGroupFilters, toggleAccessGroupColumnSort, AccessGroupTableName } from './store';
import { Toggle } from '../../shared/toggle';
import { CustomTooltip, OverflowTooltip } from '../../shared/tooltip';
import { ColumnSort, FilterDropdownOptions, SortOrder, TableFilters, TableFilterType } from '../../shared/table/types';
import { IconButton } from '../../shared/button/IconButton';
import { Position } from '../../shared/modal/PositionModal';
import { TableFilterModal } from '../../shared/modal/TableFilterModal';
import { Icon } from '../../shared/icon/Icon';
import { Agency, Eye, Home, Filter, Sort, SortAscending, SortDescending } from '../../shared/icons';
import { useAppDispatch } from '../../../hooks/react-redux';

const { frenchLight, fixedWhite } = styles;

export interface AccessGroupTableRowData {
    width: string;
    title: string;
    column: string;
    isIconHeader?: boolean;
}

interface AccessGroupTableHeaderProps {
    allSelected: boolean;
    toggleSelectAll: () => void;
    textFilterColumns: string[];
    tableHeaders: AccessGroupTableRowData[];
    selectAllLabel: string;
    filters: TableFilters;
    tableName: AccessGroupTableName;
    columnSort?: ColumnSort;
    filterDropdownOptions?: FilterDropdownOptions;
}

export const AccessGroupTableHeader: React.FC<AccessGroupTableHeaderProps> = ({ allSelected, toggleSelectAll, textFilterColumns, tableHeaders, selectAllLabel, filters, tableName, columnSort, filterDropdownOptions = {} }) => {
    const dispatch = useAppDispatch();

    const [position, setPosition] = useState<Position | null>(null);
    const [filterFieldOpen, setFilterFieldOpen] = useState<string | null>(null);

    const sortColumn = useCallback((sortColumn: string) => {
        if (sortColumn === 'role') {
            noop();
        }
        let orderToSort = SortOrder.ASCENDING;
        if (columnSort) {
            const { field, order } = columnSort;
            orderToSort = sortColumn === field && order === SortOrder.ASCENDING ? SortOrder.DESCENDING : SortOrder.ASCENDING;
        }
        dispatch(toggleAccessGroupColumnSort(tableName, { field: sortColumn, order: orderToSort }));
    }, [columnSort, dispatch, tableName]);

    const getSortIcon = useCallback((field: string) => {
        if (columnSort && columnSort.field === field) {
            return columnSort.order === SortOrder.ASCENDING ? SortDescending : SortAscending;
        }
        return Sort;
    }, [columnSort]);

    const selectAllTooltip = useMemo(() => allSelected ? 'Deselect' : 'Select', [allSelected]);

    const openFilterModal = (x: number, y: number, column: string) => {
        setPosition({ x, y });
        setFilterFieldOpen(column);
    };

    const closeFilterModal = () => {
        setPosition(null);
        setFilterFieldOpen(null);
    };

    const columnFilterExists = useCallback((field: string) => !!filters[field] && (!isNull(filters[field].dropdown) || !!filters[field].text), [filters]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setAccessGroupFilters(tableName, key, value, type)), [dispatch, tableName]);

    return (
        <div className={styles.accessGroupHeaderWrapper}>
            <div className={styles.tableHeader}>
                {tableHeaders.map(({ width, title, column, isIconHeader }, index) => (
                    isIconHeader ?
                        <div className={styles.tableHeaderCell} style={{ width }} key={index}>
                            <div className={styles.iconsCell}>
                                <CustomTooltip overlayText='My Home Entities' placement='top'>
                                    <div className={styles.headerIcon}>
                                        <Icon icon={Home} />
                                    </div>
                                </CustomTooltip>
                                <CustomTooltip overlayText='Agency Entities' placement='top'>
                                    <div className={styles.headerIcon}>
                                        <Icon icon={Agency} />
                                    </div>
                                </CustomTooltip>
                                <CustomTooltip overlayText='Entities on Watchlist' placement='top'>
                                    <div className={styles.headerIcon}>
                                        <Icon icon={Eye} />
                                    </div>
                                </CustomTooltip>
                            </div>
                            <div className={styles.sortWrapper}>
                                <IconButton icon={Filter} onClick={e => openFilterModal(e.clientX, e.clientY, column)} color={fixedWhite} fontSize={20} margin={'0 5px 0 0'} showIndicator={columnFilterExists(column)} />
                            </div>
                        </div>
                        :
                        <div className={styles.tableHeaderCell} style={{ width }} key={index}>
                            <OverflowTooltip className={styles.tableHeaderTitle} overlayText={title} />
                            <div className={styles.sortWrapper}>
                                {title === 'Included' &&
                                    <CustomTooltip overlayText={`${selectAllTooltip} All ${selectAllLabel}`} placement='top'>
                                        <div className={styles.selectAllWrapper}>
                                            <Toggle
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                onColour={frenchLight}
                                            />
                                        </div>
                                    </CustomTooltip>
                                }
                                {column !== 'role' ? <IconButton icon={getSortIcon(column)} onClick={() => sortColumn(column)} color={fixedWhite} fontSize={20} /> : null}
                                {!['Included', 'Role'].includes(title) && <IconButton icon={Filter} onClick={e => openFilterModal(e.clientX, e.clientY, column)} color={fixedWhite} fontSize={20} margin={'0 5px 0 0'} showIndicator={columnFilterExists(column)} />}
                            </div>
                        </div>
                ))}
            </div>
            {!isNull(filterFieldOpen) && !isNull(position) &&
                <TableFilterModal
                    position={position}
                    field={filterFieldOpen}
                    closeModal={closeFilterModal}
                    testId={`access-group-${tableName}-table-filter`}
                    filters={filters}
                    updateFilter={updateFilter}
                    filterDropdownOptions={filterDropdownOptions}
                    textFilterVisible={textFilterColumns.includes(filterFieldOpen)}
                />
            }
        </div>
    );
};
